// assets/floating-atc-bar.js

class FloatingATCBar extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.targetSelector = this.dataset.productInfoWrapperSelector;
    this.targetElement = document.querySelector(
      `${this.targetSelector}[data-section-id="${this.sectionId}"]`
    ); // Ensure we target the element within the correct section

    // Attempt to find target using a more specific selector if the first fails
    if (!this.targetElement) {
        const mainProductElement = this.closest('product-info'); // Find the parent product-info element
        if(mainProductElement) {
            this.targetElement = mainProductElement.querySelector(this.targetSelector);
        }
    }

    if (!this.targetElement) {
      console.error(`FloatingATCBar: Target element "${this.targetSelector}" not found within section ${this.sectionId}.`);
      return;
    }

    // Select elements relative to the specific floating-atc-bar instance
    this.floatBarDiv = this.querySelector(`#FloatingATC-${this.sectionId}`);
    if (!this.floatBarDiv) {
        console.error(`FloatingATCBar: Could not find the bar div #FloatingATC-${this.sectionId}.`);
        return;
    }

    // Select main form elements more robustly, relative to the section
    const mainProductInfo = document.getElementById(`ProductInfo-${this.sectionId}`);
    if (!mainProductInfo) {
        console.error(`FloatingATCBar: Could not find main product info container #ProductInfo-${this.sectionId}.`);
        return;
    }

    this.mainFormVariantInput = mainProductInfo.querySelector('input[name="id"]');
    this.mainFormSubmitButton = mainProductInfo.querySelector('.product-form__submit:not(.product-form__submit--float)'); // Exclude float button

    this.floatVariantInput = this.floatBarDiv.querySelector('.product-variant-id--float');
    this.floatSubmitButton = this.floatBarDiv.querySelector('.product-form__submit--float');
    this.floatSubmitText = this.floatBarDiv.querySelector('.product-form__submit-text');

    if (!this.mainFormVariantInput || !this.mainFormSubmitButton || !this.floatVariantInput || !this.floatSubmitButton || !this.floatSubmitText) {
       console.error(`FloatingATCBar: Could not find all required form elements for section ${this.sectionId}. Check selectors.`);
       console.log("Main Variant Input:", this.mainFormVariantInput);
       console.log("Main Submit Button:", this.mainFormSubmitButton);
       console.log("Float Variant Input:", this.floatVariantInput);
       console.log("Float Submit Button:", this.floatSubmitButton);
       console.log("Float Submit Text:", this.floatSubmitText);
       return;
    }

    // Debounce resize handler
    this.debounceResize = debounce(this.checkScrollPosition.bind(this), 50);

    this.initObserver();
    this.addEventListeners();
    this.checkScrollPosition(); // Initial check in case the target is already out of view
  }

  initObserver() {
    const observerOptions = {
      rootMargin: '0px 0px 0px 0px', // No margin adjustment needed
      threshold: 0.5 // Trigger when visibility crosses 50%
    };

    this.observer = new IntersectionObserver(
      this.handleIntersection.bind(this),
      observerOptions
    );
    this.observer.observe(this.targetElement);
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      // If target is *not* intersecting (scrolled past), show the bar
      // entry.boundingClientRect.bottom < 0 ensures it's scrolled *above* the viewport
      // New logic: Show if less than 50% visible AND scrolled past the top
      if (entry.intersectionRatio < 0.5 && entry.boundingClientRect.top < 0) {
        this.show();
      } else {
        // Hide otherwise (more than 50% visible OR not scrolled past the top)
        this.hide();
      }
    });
  }

  // Fallback check in case IntersectionObserver isn't perfect or for initial load
  checkScrollPosition() {
     if (!this.targetElement || !this.floatBarDiv) return;
     try {
        const targetRect = this.targetElement.getBoundingClientRect();
        // If the center of the target is above the viewport top, show the bar
        if (targetRect.top + targetRect.height / 2 < 0) {
            this.show();
        } else {
            this.hide();
        }
     } catch (e) {
         console.error("FloatingATCBar: Error in checkScrollPosition", e);
         this.hide(); // Hide if error occurs
     }
   }

  syncState() {
    if (!this.mainFormVariantInput || !this.mainFormSubmitButton || !this.floatVariantInput || !this.floatSubmitButton || !this.floatSubmitText) {
      // console.warn(`FloatingATCBar (${this.sectionId}): Skipping sync due to missing elements.`);
      return;
    }

    const mainVariantId = this.mainFormVariantInput.value;
    const mainButtonDisabled = this.mainFormSubmitButton.hasAttribute('disabled');
    const mainButtonText = this.mainFormSubmitButton.querySelector('span:not(.product-form__spinner)')?.textContent.trim(); // Get text, ignore spinner span

    // Sync Variant ID
    this.floatVariantInput.value = mainVariantId;

    // Sync Button Disabled State
    if (mainButtonDisabled) {
      this.floatSubmitButton.setAttribute('disabled', '');
    } else {
      this.floatSubmitButton.removeAttribute('disabled');
    }

    // Sync Button Text (Sold Out / Unavailable / Add to Cart)
    if (mainButtonText) {
       this.floatSubmitText.textContent = mainButtonText;
    }

    // Also sync the hidden quantity input value if it exists in the main form
    const mainInfoContainer = document.getElementById(`ProductInfo-${this.sectionId}`);
    const mainQuantityInput = mainInfoContainer?.querySelector('.quantity__input:not(.quantity__input--float)');
    const floatQuantityInput = this.floatBarDiv.querySelector('.quantity__input--float');
    if (mainQuantityInput && floatQuantityInput) {
        floatQuantityInput.value = mainQuantityInput.value;
        // Also sync min attribute if necessary
        if (mainQuantityInput.hasAttribute('min')) {
          floatQuantityInput.setAttribute('min', mainQuantityInput.getAttribute('min'));
        }
    }
  }

  addEventListeners() {
     const productInfoContainer = document.getElementById(`ProductInfo-${this.sectionId}`);
     if (productInfoContainer) {
       // Using event delegation on the container for variant changes
       // This relies on the main product form dispatching a 'variant:change' event
       // or similar. If not, MutationObserver is the fallback.
       productInfoContainer.addEventListener('variant:change', this.syncState.bind(this));

       // Fallback: Use MutationObserver if 'variant:change' isn't reliable
       this.mutationObserver = new MutationObserver((mutations) => {
           // Check if the relevant attributes changed
           let syncNeeded = false;
           for (const mutation of mutations) {
               if (mutation.type === 'attributes' && (mutation.attributeName === 'disabled' || mutation.attributeName === 'value')) {
                   syncNeeded = true;
                   break;
               }
               // Add more specific checks if needed, e.g., for text content changes
           }
           if (syncNeeded) {
                this.syncState();
           }
           // Basic check for any subtree change might be too broad but ensures sync
           // requestAnimationFrame(() => this.syncState());
       });
       this.mutationObserver.observe(productInfoContainer, {
           childList: true, // Observe direct children changes
           subtree: true,   // Observe deeper descendants
           attributes: true, // Observe attribute changes
           attributeFilter: ['disabled', 'value'], // Focus on relevant attributes
           characterData: true // Observe text changes (e.g., button text)
       });
     } else {
        console.error(`FloatingATCBar: ProductInfo container #ProductInfo-${this.sectionId} not found.`);
     }

     window.addEventListener('scroll', this.checkScrollPosition.bind(this), { passive: true });
     window.addEventListener('resize', this.debounceResize);

     // Initial sync after a short delay for main form JS to initialize
     setTimeout(() => this.syncState(), 500);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if(this.mutationObserver){
        this.mutationObserver.disconnect();
    }
    // Remove specific event listener if added
    const productInfoContainer = document.getElementById(`ProductInfo-${this.sectionId}`);
    if (productInfoContainer) {
       productInfoContainer.removeEventListener('variant:change', this.syncState.bind(this));
    }
    window.removeEventListener('scroll', this.checkScrollPosition.bind(this));
    window.removeEventListener('resize', this.debounceResize);
  }

  show() {
    if (!this.floatBarDiv) return;
    this.floatBarDiv.classList.add('is-visible');
    this.floatBarDiv.setAttribute('aria-hidden', 'false');
  }

  hide() {
    if (!this.floatBarDiv) return;
    this.floatBarDiv.classList.remove('is-visible');
    this.floatBarDiv.setAttribute('aria-hidden', 'true');
  }
}

// Simple debounce function
function debounce(func, wait, immediate) {
  var timeout;
  return function() {
    var context = this, args = arguments;
    var later = function() {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    var callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
};

// Define the custom element
if (!customElements.get('floating-atc-bar')) {
  customElements.define('floating-atc-bar', FloatingATCBar);
} 